import React from "react"
import { get } from "lodash"
import { boolean } from "yup"
import { H4, P } from "@4cplatform/elements/Typography"
import { ComplianceNote } from "@4cplatform/elements/Molecules"
import DisclosureNotes from "../../../../../../DisclosureNotes"
import NoteFormTemplate from "./noteFormTemplate"
import NoteDisplayTemplate from "./noteDisplayTemplate"
import Audit from "./Audit"

export const config = data => {
  const cutover = new Date(process.env.REACT_APP_BUPA_FMU_DISCLOSURE_QUESTIONS_GO_LIVE_DATE)
  const journeyCreatedAt = get(data, "journey.created_at")

  const legacyQuestions = [
    {
      key: "heart_or_cardiovascular_disorders",
      label:
        "Heart or cardiovascular disorders eg coronary artery disease, chest pains, circulation problems, varicose veins, high blood pressure, venous ulcers"
    },
    {
      key: "glandular_disorders",
      label: "Glandular disorders eg diabetes, thyroid, hormonal problems"
    },
    {
      key: "breathing_or_respiratory_disorders",
      label:
        "Breathing or respiratory disorders eg asthma, bronchitis, shortness of breath, chest infections, colds, flu"
    },
    {
      key: "ears_nose_throat_or_eye_problems",
      label:
        "Ears, nose, throat, or eye problems eg hayfever, tonsillitis, sinusitis, cataracts, eye infections, deafness, ear infections"
    },
    {
      key: "stomach_intestines_liver_or_gallbladder",
      label:
        "Stomach, intestines, liver or gallbladder eg ulcer, colitis, repeated indigestion, irritable bowel, change in bowel habits, hepatitis, piles, rectal bleeding"
    },
    {
      key: "cancer_tumours_growths_cysts_or_moles_that_itch_or_bleed",
      label: "Cancer, tumours, growths, cysts, or moles that itch or bleed"
    },
    {
      key: "skin_problems",
      label: "Skin problems eg eczema, rashes, psoriasis, acne"
    },
    {
      key: "brain_or_nervous_system_disorders",
      label:
        "Brain or nervous system disorders eg stroke, migraines, repeated headaches, MS, epilepsy, nerve pain, fits"
    },
    {
      key: "muscle_or_skeletal_problems",
      label:
        "Muscle or skeletal problems eg arthritis, cartilage and ligament problems, back and neck problems, sprains, joint replacements, gout, sciatica"
    },
    {
      key: "urinary_problems",
      label:
        "Urinary problems eg bladder, kidney or prostate problems, urinary infections, incontinence"
    },
    {
      key: "blood_disorders",
      label: "Blood disorders eg anaemia, hepatitis, HIV, abnormal blood tests"
    },
    {
      key: "reproductive_system_problems",
      label:
        "Reproductive system problems eg pregnancy and/or childbirth problems, heavy or irregular periods, fibroids, endometriosis, infertility, abnormal smears, menopause, caesarean section, low testosterone, erectile dysfunction, low sperm count"
    },
    {
      key: "dental_problems",
      label: "Dental problems eg wisdom teeth, abscess, gingivitis"
    },
    {
      key: "allergies",
      label: "Allergies eg hayfever, pet allergies, food allergies"
    },
    {
      key: "psychological_disorders",
      label:
        "Psychological disorders eg depression, schizophrenia, anorexia, bulimia, compulsive disorders, stress, anxiety"
    },
    {
      key: "undiagnosed_symptoms",
      label:
        "Undiagnosed symptoms eg chest pain, fatigue, weight loss, dizziness, joint pain, change in bowel habit, shortness of breath, abdominal pain, rectal bleeding, lumps"
    },
    {
      key: "applicant_member_taking_any_medicines_prescribed_or_otherwise",
      label: "Are you or any applicant/member taking any medicines, prescribed or otherwise?"
    },
    {
      key: "covered_experienced_symptoms",
      label:
        "Within the last three months has anyone to be covered experienced symptoms of ANY health problems for which medical advice has not yet been sought?"
    },
    {
      key: "past_history_of_joint_replacements_heart_conditions_or_strokes",
      label:
        "Has anyone to be covered EVER had any past history of joint replacements, heart conditions, or strokes?"
    },
    {
      key: "other_information_relating",
      label:
        "Is there any other information relating to your health that has not yet been prompted by the questions listed 1 to 19?"
    }
  ]

  const newQuestions = [
    {
      key: "muscle_bone_or_joint_conditions",
      label:
        "Has your client had any symptoms or a condition affecting their back, neck, joints, muscles, bones, nerves or any other condition that affects normal movement (For example: arthritis, cartilage or ligament problems, sprains, joint replacement, gout, sciatica):"
    },
    {
      key: "heart_and_circulation_conditions",
      label:
        "Has your client experienced shortness of breath, heart palpitations, swollen ankles, angina, heart attack, stroke, mini-stroke (also called TIA) or any related symptoms? (For example: high blood pressure, high cholesterol, atrial fibrillation, heart failure, heart disease, chest pains, coronary artery disease)"
    },
    {
      key: "pelvic_conditions",
      label:
        "Has your client had any problems with their bladder, bowels, kidneys or prostate? Do they have heavy or irregular periods? Has your client had a caesarean section? (For example: urinary tract infections, irritable bowel, incontinence, endometriosis, fibroids, pregnancy or childbirth problems)"
    },
    {
      key: "eye_ear_and_teeth_conditions",
      label:
        "Has your client had any problems with their vision, hearing, balance, an impacted tooth, or retained tooth root? (For example: cataracts, glaucoma, infections, abscess)"
    },
    {
      key: "mental_health_conditions",
      label:
        "Has your client talked with a GP, therapist, counsellor or any other health professional about their mental health? (For example: stress, depression, fatigue, anxiety, anorexia, bulimia, compulsive disorders, schizophrenia, bipolar disorder):"
    },
    {
      key: "lung_or_other_breathing_conditions",
      label:
        "Has your client had any problems with breathing or their respiratory system? (For example: breathlessness, asthma, bronchitis, chronic obstructive pulmonary disease (COPD), pulmonary fibrosis, pneumonia or chest infections)"
    },
    {
      key: "cancer_conditions",
      label:
        "Has your client ever been diagnosed with or had treatment for cancer, or had a test that shows they may have cancer? (For example: tumours, abnormal smears, raised PSA (prostate-specific antigen) levels):"
    },
    {
      key: "any_other_ongoing_conditions",
      label:
        "Does your client have any other symptoms or health conditions for which they need ongoing prescription medication, regular medical tests, examinations or consultations?:"
    },
    {
      key: "any_planned_treatment",
      label:
        "Does your client have any other health condition or symptoms for which they intend to seek investigation or treatment in the future or have currently received investigation/treatment for?"
    }
  ]

  let journeyDate = new Date()

  // date condition for cutover
  if (journeyCreatedAt) {
    journeyDate = new Date(journeyCreatedAt)
  }

  // Determine which set of questions to use based on the current date
  const questions = journeyDate >= cutover ? newQuestions : legacyQuestions

  return {
    title: "Bupa FMU Disclosures",
    sections: [
      {
        key: "section_1",
        components: [
          {
            component: Audit,
            skipDataMap: true
          },
          {
            key: "compliance_note",
            component: ComplianceNote,
            componentProps: {
              children: (
                <>
                  <H4 margin="0 0 1rem">Obtaining medical reports from a GP</H4>
                  <P>
                    For any of the medical conditions or symptoms listed in questions 1 to
                    {journeyDate >= cutover ? " 9" : " 20"} please indicate if:
                  </P>
                  <P margin="0">
                    You or anyone to be covered on your membership has seen a GP or other healthcare
                    professional within the last two years.
                  </P>
                  <P>
                    You or anyone to be covered on your membership has been admitted to hospital,
                    had an operation OR any investigations (for example scan, X-ray, blood test,
                    within the last seven years.
                  </P>
                </>
              ),
              type: "info"
            },
            skipDataMap: true
          },
          ...questions.map((item, i) => ({
            ...item,
            label: `${i + 1}. ${item.label}`,
            name: item.key,
            initialValue: get(data, `page.data.${item.key}`, false),
            validationSchema: boolean().required("MISSING_REQUIRED_FIELD"),
            component: DisclosureNotes,
            componentProps: {
              options: [
                { order: 0, label: "No", value: false },
                { order: 1, label: "Yes", value: true }
              ],
              noteFormTemplate: NoteFormTemplate,
              noteDisplayTemplate: NoteDisplayTemplate,
              isHorizontal: true,
              isRequired: true
            }
          }))
        ]
      }
    ]
  }
}
